import { Controller } from "@hotwired/stimulus"

// Manages file selection for document requests where multiple specific
// documents are requested. Each document has its own compact card that
// users can click or drag-and-drop to select a file.
export default class extends Controller {
  static targets = ["documentSlot", "submitButton"]
  static values = {
    maxFileSize: { type: Number, default: 50_000_000 },
    documents: Array // List of requested document names
  }

  connect() {
    this.updateSubmitState()
  }

  selectFile(event) {
    const slot = event.currentTarget.closest("[data-document-request-upload-target='documentSlot']")
    const fileInput = slot.querySelector("input[type='file']")
    fileInput.click()
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    const slot = event.currentTarget
    slot.classList.add("border-blue-400", "bg-blue-50")
    slot.classList.remove("border-gray-200", "border-green-200", "bg-green-50")
  }

  handleDragLeave(event) {
    event.preventDefault()
    const slot = event.currentTarget
    slot.classList.remove("border-blue-400", "bg-blue-50")

    // Restore appropriate state
    const fileInput = slot.querySelector("input[type='file']")
    if (fileInput?.files?.length > 0) {
      slot.classList.add("border-green-200", "bg-green-50")
    } else {
      slot.classList.add("border-gray-200")
    }
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    const slot = event.currentTarget
    slot.classList.remove("border-blue-400", "bg-blue-50")

    const files = event.dataTransfer.files
    if (!files || files.length === 0) return

    const file = files[0] // Only take the first file

    if (file.size > this.maxFileSizeValue) {
      const maxMb = Math.round(this.maxFileSizeValue / 1_000_000)
      alert(`Bestanden groter dan ${maxMb}MB worden niet ondersteund`)
      slot.classList.add("border-gray-200")
      return
    }

    // Update the file input
    const fileInput = slot.querySelector("input[type='file']")
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    fileInput.files = dataTransfer.files

    this.updateSlotUI(slot, file)
    this.updateSubmitState()
  }

  handleFileChange(event) {
    const fileInput = event.target
    const slot = fileInput.closest("[data-document-request-upload-target='documentSlot']")
    const file = fileInput.files[0]

    if (!file) return

    if (file.size > this.maxFileSizeValue) {
      const maxMb = Math.round(this.maxFileSizeValue / 1_000_000)
      alert(`Bestanden groter dan ${maxMb}MB worden niet ondersteund`)
      fileInput.value = ""
      return
    }

    this.updateSlotUI(slot, file)
    this.updateSubmitState()
  }

  updateSlotUI(slot, file) {
    const emptyStates = slot.querySelectorAll(".empty-state")
    const filledStates = slot.querySelectorAll(".filled-state")
    const fileNameEl = slot.querySelector(".assigned-file-name")
    const fileSizeEl = slot.querySelector(".assigned-file-size")

    if (file) {
      emptyStates.forEach(el => el.classList.add("hidden"))
      filledStates.forEach(el => el.classList.remove("hidden"))
      slot.classList.remove("border-gray-200", "border-blue-400", "bg-blue-50")
      slot.classList.add("border-green-200", "bg-green-50")
      if (fileNameEl) fileNameEl.textContent = file.name
      if (fileSizeEl) fileSizeEl.textContent = `(${this.formatFileSize(file.size)})`
    } else {
      emptyStates.forEach(el => el.classList.remove("hidden"))
      filledStates.forEach(el => el.classList.add("hidden"))
      slot.classList.add("border-gray-200")
      slot.classList.remove("border-green-200", "bg-green-50", "border-blue-400", "bg-blue-50")
    }
  }

  removeFile(event) {
    event.stopPropagation()
    const slot = event.currentTarget.closest("[data-document-request-upload-target='documentSlot']")
    const fileInput = slot.querySelector("input[type='file']")
    fileInput.value = ""
    this.updateSlotUI(slot, null)
    this.updateSubmitState()
  }

  updateSubmitState() {
    if (!this.hasSubmitButtonTarget) return

    const allFilled = this.documentSlotTargets.every(slot => {
      if (slot.dataset.optional === "true") return true

      const fileInput = slot.querySelector("input[type='file']")
      return fileInput?.files?.length > 0
    })

    this.submitButtonTarget.disabled = !allFilled

    if (allFilled) {
      this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    } else {
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
    return (bytes / (1024 * 1024)).toFixed(1) + " MB"
  }
}
